/* import __COLOCATED_TEMPLATE__ from './draggable-connector.hbs'; */
/* RESPONSIBLE TEAM: team-proactive-support */
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';

import Component from '@glimmer/component';
import type Graph from '../../../models/graph-editor/graph';
import type Node from '../../../models/graph-editor/node';
import type Edge from '../../../models/graph-editor/edge';
import BLANK_IMAGE from '../../../data/blank-image';

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
  Blocks: {
    default: [];
  };
}

interface Args {
  predecessor: Node;
  dataObject: any;
  canStartDragging?(): boolean;
  didStartDragging?(edge: Edge): void;
  didEndDragging?(): void;
}

export default class GraphEditorEdgeDraggableConnectorComponent extends Component<Signature> {
  blankDragImage: HTMLImageElement = new Image(1, 1);
  edge?: Edge;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.args = args;
    this.blankDragImage.src = BLANK_IMAGE;
  }

  get graph(): Graph {
    return this.args.predecessor.graph;
  }

  get successor(): Node | undefined {
    if (this.graph.state.hoveredNodes) {
      return this.graph.state.hoveredNodes[0];
    }
    return undefined;
  }

  @action onDragStart(event: DragEvent): void {
    if (this.args.canStartDragging) {
      if (this.args.canStartDragging()) {
        this._onDragStart(event);
      }
    } else {
      this._onDragStart(event);
    }
  }

  @action onDragEnd(_: DragEvent): void {
    if (this.edge && isEmpty(this.edge?.successor)) {
      if (this.successor && this.graph.hooks.canInsertEdge(this.args.predecessor, this.successor)) {
        this.edge.successor = this.successor;
        this.graph.hooks.didInsertEdge(this.edge);
      } else {
        this.graph.actionsToUndo.popObject();
        this.graph.edges.removeObject(this.edge);
      }
    }

    this.graph.state.draggingEdge = undefined;

    if (this.args.didEndDragging) {
      this.args.didEndDragging();
    }
  }

  _onDragStart(event: DragEvent): void {
    this.graph.trackActions(() => {
      // Stop any ongoing selection area
      this.graph.state.selectionStartCoordinates = undefined;

      // Deselect any selected nodes – we want to drag an edge now
      this.graph.state.selectedNodes.clear();

      this.edge = this.graph.addEdge({
        predecessor: this.args.predecessor,
        dataObject: this.args.dataObject,
      });

      this.graph.state.draggingEdge = this.edge;

      // Firefox requires data to be set for the element to start dragging
      event.dataTransfer?.setData('text/plain', '');

      // Set the data image to be blank so the browser doesn't default
      // to the 'ghost image' effect while dragging.
      event.dataTransfer?.setDragImage(this.blankDragImage, 1, 1);

      if (this.args.didStartDragging) {
        this.args.didStartDragging(this.edge);
      }
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'GraphEditor::Edge::DraggableConnector': typeof GraphEditorEdgeDraggableConnectorComponent;
  }
}
